import { useState } from "react";
import { PROCESSING_STEP_LABELS, type ProcessingStep } from "@gb/contracts";
import type { AdminBackfillStep } from "@gb/admin-contracts";
import { Button } from "@/components/ui/button";
import { useBackfill, useStartBackfill } from "./projects-api";

/**
 * The backfill (2026-09-26, tracker RET-09): the steps whose code has changed
 * since they ran on some of the project's files, and a button per step that
 * redoes it on those files.
 *
 * A person starts it here, one step at a time. Nothing starts it after a
 * deploy: that is the tracker's default for RET-09's question, and the owner
 * can change it. A backfill of the parse reads every file again, pictures and
 * all, so the count and what it redoes are shown first, and the button asks
 * once more before anything is queued.
 */
export function BackfillPanel({
  tenantId,
  projectId,
}: {
  tenantId: string;
  projectId: string;
}) {
  const steps = useBackfill(tenantId, projectId);
  const start = useStartBackfill(tenantId, projectId);
  const [confirming, setConfirming] = useState<ProcessingStep | null>(null);

  if (steps.isPending)
    return (
      <p className="mb-4 text-[0.82rem] text-muted">
        Reading the step versions…
      </p>
    );
  if (steps.isError) {
    return (
      <p role="alert" className="mb-4 text-[0.82rem] font-semibold text-danger">
        {steps.error.message}
      </p>
    );
  }

  return (
    <section
      aria-label="Backfill"
      className="mb-5 rounded-card border border-border bg-surface px-4 py-3"
    >
      <h3 className="font-display text-[0.78rem] font-semibold tracking-[0.08em] text-muted uppercase">
        Backfill
      </h3>
      {steps.data.length === 0 ? (
        <p className="mt-1 text-[0.82rem] text-muted">
          Every step ran on these files with the code as it is now.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {steps.data.map((step) => (
            <BackfillRow
              key={step.step}
              step={step}
              confirming={confirming === step.step}
              busy={start.isPending && start.variables === step.step}
              onAsk={() => setConfirming(step.step)}
              onCancel={() => setConfirming(null)}
              onStart={() => {
                setConfirming(null);
                start.mutate(step.step);
              }}
            />
          ))}
        </ul>
      )}
      {start.data && (
        <p role="status" className="mt-2 text-[0.82rem] text-ink">
          {PROCESSING_STEP_LABELS[start.data.step]}: {jobs(start.data.queued)}{" "}
          queued.
          {start.data.waiting > 0
            ? ` ${files(start.data.waiting)} ${start.data.waiting === 1 ? "waits" : "wait"} for a job of ${start.data.waiting === 1 ? "its" : "their"} own, which does the step again.`
            : ""}
        </p>
      )}
      {start.error && (
        <p
          role="alert"
          className="mt-2 text-[0.82rem] font-semibold text-danger"
        >
          {start.error.message}
        </p>
      )}
    </section>
  );
}

function BackfillRow({
  step,
  confirming,
  busy,
  onAsk,
  onCancel,
  onStart,
}: {
  step: AdminBackfillStep;
  confirming: boolean;
  busy: boolean;
  onAsk: () => void;
  onCancel: () => void;
  onStart: () => void;
}) {
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-1 border-t border-border pt-2 first:border-t-0 first:pt-0">
      <div className="min-w-0 flex-1">
        <p className="text-[0.85rem] text-ink">
          <span className="font-semibold">
            {PROCESSING_STEP_LABELS[step.step]}
          </span>{" "}
          <span className="font-mono text-[0.75rem] text-muted">
            {step.step} v{step.oldestVersion} → v{step.codeVersion}
          </span>
        </p>
        <p className="text-[0.8rem] text-muted">
          {step.files === null
            ? step.redoes
            : `${files(step.files)} behind. ${step.redoes}`}
        </p>
      </div>
      {step.job !== null && step.files !== null && step.files > 0 && (
        <div className="flex items-center gap-2">
          {confirming ? (
            <>
              <Button size="sm" disabled={busy} onClick={onStart}>
                Queue {jobs(step.files)}
              </Button>
              <Button size="sm" variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={onAsk}
            >
              Backfill
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

function files(n: number): string {
  return n === 1 ? "1 file" : `${n} files`;
}

function jobs(n: number): string {
  return n === 1 ? "1 job" : `${n} jobs`;
}
